import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { validateLoginForm } from '../utils/validation';
import { CheckCircle2, Star, Users, UserPlus } from 'lucide-react';
import { resetRateLimit } from '../utils/security';
import { PatientSignupPage } from './PatientSignupPage';
export function LoginPage() {
  const {
    login
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [showPatientSignup, setShowPatientSignup] = useState(false);
  useEffect(() => {
    resetRateLimit();
    // Trigger entrance animations
    requestAnimationFrame(() => setMounted(true));
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    const validation = validateLoginForm(email, password);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setLoginError(result.error || 'Error al iniciar sesión');
    }
    setIsLoading(false);
  };
  // Show patient signup page if requested
  if (showPatientSignup) {
    return <PatientSignupPage onBackToLogin={() => setShowPatientSignup(false)} data-id="element-4649" />;
  }
  return <div className="min-h-screen w-full flex overflow-hidden bg-gray-50 dark:bg-gray-900" data-id="element-4650">
      {/* Inline keyframes for animations */}
      <style data-id="element-4651">{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulseOrb {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.2); opacity: 0.5; }
        }
        @keyframes pulseOrb2 {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(1.1); opacity: 0.4; }
        }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
        .animate-fadeIn { animation: fadeIn 0.8s ease-out forwards; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-float-delay-1 { animation: float 3s ease-in-out 0.2s infinite; }
        .animate-float-delay-2 { animation: float 3s ease-in-out 0.4s infinite; }
        .animate-pulseOrb { animation: pulseOrb 8s ease-in-out infinite; }
        .animate-pulseOrb2 { animation: pulseOrb2 10s ease-in-out 1s infinite; }
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
      `}</style>

      {/* Left Decorative Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-teal-900 overflow-hidden items-center justify-center" data-id="element-4652">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-4653">
          <img src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&auto=format&fit=crop" alt="Spa Wellness Background" className="w-full h-full object-cover opacity-60 mix-blend-overlay" data-id="element-4654" />
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/90 via-teal-800/80 to-cyan-900/90 mix-blend-multiply" data-id="element-4655" />
        </div>

        {/* Animated Decorative Orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-teal-400/20 rounded-full blur-3xl z-10 animate-pulseOrb" data-id="element-4656" />
        <div className="absolute bottom-[-5%] right-[-5%] w-[500px] h-[500px] bg-cyan-400/20 rounded-full blur-3xl z-10 animate-pulseOrb2" data-id="element-4657" />

        {/* Content Container */}
        <div className={`relative z-20 max-w-lg px-12 text-center transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} data-id="element-4658">
          <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-md rounded-2xl mb-8 ring-1 ring-white/20 shadow-lg" data-id="element-4659">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-teal-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4660">
              <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-4661" />
              <path d="M12 6c0-2.5 2-4 4-4" data-id="element-4662" />
              <path d="M12 6c0-2.5-2-4-4-4" data-id="element-4663" />
            </svg>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6 tracking-tight leading-tight" data-id="element-4664">
            Bienestar y Belleza <br data-id="element-4665" />
            <span className="text-teal-200" data-id="element-4666">en Armonía</span>
          </h2>
          <p className="text-lg text-teal-50/90 leading-relaxed mb-12 font-light" data-id="element-4667">
            Gestione su centro con elegancia y eficiencia. La plataforma
            integral para profesionales del bienestar.
          </p>

          {/* Floating Glass Cards */}
          <div className="grid grid-cols-3 gap-4" data-id="element-4668">
            <div className="animate-float bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 text-center hover:scale-105 transition-transform duration-300" data-id="element-4669">
              <Users className="w-6 h-6 text-teal-200 mx-auto mb-2" data-id="element-4670" />
              <div className="text-2xl font-bold text-white" data-id="element-4671">4000+</div>
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mt-1" data-id="element-4672">
                Clientes
              </div>
            </div>

            <div className="animate-float-delay-1 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 text-center hover:scale-105 transition-transform duration-300" data-id="element-4673">
              <CheckCircle2 className="w-6 h-6 text-teal-200 mx-auto mb-2" data-id="element-4674" />
              <div className="text-2xl font-bold text-white" data-id="element-4675">150+</div>
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mt-1" data-id="element-4676">
                Servicios
              </div>
            </div>

            <div className="animate-float-delay-2 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 text-center hover:scale-105 transition-transform duration-300" data-id="element-4677">
              <Star className="w-6 h-6 text-teal-200 mx-auto mb-2" data-id="element-4678" />
              <div className="text-2xl font-bold text-white" data-id="element-4679">4.9</div>
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mt-1" data-id="element-4680">
                Rating
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Login Form Panel */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 lg:p-12 relative bg-white/50 dark:bg-gray-900/50" data-id="element-4681">
        {/* Mobile Background Decoration */}
        <div className="absolute inset-0 lg:hidden z-0 overflow-hidden pointer-events-none" data-id="element-4682">
          <div className="absolute top-[-20%] right-[-20%] w-96 h-96 bg-teal-200/30 rounded-full blur-3xl" data-id="element-4683" />
          <div className="absolute bottom-[-20%] left-[-20%] w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl" data-id="element-4684" />
        </div>

        <div className={`w-full max-w-md relative z-10 transition-all duration-500 ${mounted ? 'opacity-100' : 'opacity-0'}`} data-id="element-4685">
          {/* Logo Section */}
          <div className={`text-center mb-10 transition-all duration-600 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{
          transitionDelay: '0.1s'
        }} data-id="element-4686">
            <div className="inline-block relative hover:scale-105 transition-transform duration-300" data-id="element-4687">
              <div className="absolute inset-0 bg-teal-500 rounded-full blur-lg opacity-20 animate-pulse" data-id="element-4688"></div>
              <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-24 h-24 rounded-full shadow-2xl relative z-10 border-4 border-white dark:border-gray-800 object-cover" data-id="element-4689" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-6 mb-2 tracking-tight" data-id="element-4690">
              Savidna
            </h1>
            <p className="text-lg text-teal-600 dark:text-teal-400 font-medium" data-id="element-4691">
              Centro de Bienestar y Belleza
            </p>
          </div>

          {/* Login Card */}
          <div className={`bg-white dark:bg-gray-800 rounded-3xl shadow-xl shadow-teal-900/5 p-8 lg:p-10 border border-gray-100 dark:border-gray-700 backdrop-blur-sm transition-all duration-600 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{
          paddingTop: '20px',
          paddingBottom: '20px',
          transitionDelay: '0.2s'
        }} data-id="element-4692">
            <div className="mb-8" data-id="element-4693">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4694">
                Bienvenido de nuevo
              </h2>
              <p className="text-gray-500 dark:text-gray-400" data-id="element-4695">
                Ingresa tus credenciales para acceder al panel
              </p>
            </div>

            {loginError && <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3 animate-fadeIn" data-id="element-4696">
                <div className="text-red-500 mt-0.5" data-id="element-4697">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4698">
                    <circle cx="12" cy="12" r="10" data-id="element-4699" />
                    <line x1="12" x2="12" y1="8" y2="12" data-id="element-4700" />
                    <line x1="12" x2="12.01" y1="16" y2="16" data-id="element-4701" />
                  </svg>
                </div>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium" data-id="element-4702">
                  {loginError}
                </p>
              </div>}

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4703">
              <div className="space-y-2" data-id="element-4704">
                <Input type="email" label="Correo Electrónico" placeholder="nombre@empresa.com" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} className="h-12 bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:ring-teal-500 focus:border-teal-500 transition-all" data-id="element-4705" />
              </div>

              <div className="space-y-2" data-id="element-4706">
                <Input type="password" label="Contraseña" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} error={errors.password} className="h-12 bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:ring-teal-500 focus:border-teal-500 transition-all" data-id="element-4707" />
              </div>

              <Button type="submit" className="w-full h-12 text-lg font-medium bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20 rounded-xl transition-all duration-300 transform hover:-translate-y-0.5" isLoading={isLoading} data-id="element-4708">
                Iniciar Sesión
              </Button>
            </form>

            {/* Patient Signup Link */}
            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700" data-id="element-4709">
              <button onClick={() => setShowPatientSignup(true)} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-2 border-teal-200 dark:border-teal-700 rounded-xl text-teal-700 dark:text-teal-300 font-semibold hover:from-teal-100 hover:to-cyan-100 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/30 transition-all duration-200" data-id="element-4710">
                <UserPlus className="w-5 h-5" data-id="element-4711" />
                ¿Eres paciente? Crea tu cuenta aquí
              </button>
            </div>
          </div>

          {/* Footer */}
          <p className={`text-center text-xs text-gray-400 dark:text-gray-500 mt-8 max-w-xs mx-auto leading-relaxed transition-all duration-600 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{
          transitionDelay: '0.3s'
        }} data-id="element-4712">
            <span className="flex items-center justify-center gap-1.5 mb-2" data-id="element-4713">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4714">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-4715" />
              </svg>
              Conexión segura · Contraseñas cifradas
            </span>
            © 2026 Savidna. Sistema de gestión profesional para centros de
            bienestar.
          </p>
        </div>
      </div>
    </div>;
}